import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  ReactNode,
  useCallback,
} from 'react';
import { useNavigate } from 'react-router';
import config from '../config';
import clientAccService from '../api/client/clientAccService';
import { Account } from '../types/account';
import { useAuth } from './AuthContext';

interface User {
  id: string;
  email: string;
  name?: string;
  type: 'creator' | 'partner' | 'agency';
}

interface PaginationData {
  page: number;
  page_size: number;
  count: number;
  objects: Account[];
}

interface AccountContextType {
  accounts: Account[];
  isLoading: boolean;
  pagination: PaginationData | null;
  search: string;
  page: number;
  pageSize: number;
  sortBy: string;
  refreshAccounts: () => Promise<void>;
  setSearch: (search: string) => void;
  setPage: (page: number) => void;
  setPageSize: (pageSize: number) => void;
  setSortBy: (sortBy: string) => void;
}

const AccountContext = createContext<AccountContextType | undefined>(undefined);

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (context === undefined) {
    throw new Error('useAccount must be used within an AccountProvider');
  }
  return context;
};

interface AccountProviderProps {
  children: ReactNode;
}

export const AccountProvider: React.FC<AccountProviderProps> = React.memo(({ children }) => {
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState<PaginationData | null>(null);
  const [search, setSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sortBy, setSortBy] = useState<string>('created_at');
  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const userRole = user?.role;
  const getAccounts = useCallback(async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    if (authLoading) {
      setIsLoading(true);
      return;
    }

    // Don't fetch accounts for partner users
    if (userRole === 'partner') {
      setIsLoading(false);
      return;
    }

    try {
      console.log('[AccountContext getAccounts] Params:', {
        page,
        page_size: pageSize,
        search: search.trim() || undefined,
        sort_by: sortBy,
      });

      const response = await clientAccService.getAccounts({
        page,
        page_size: pageSize,
        search: search.trim() || undefined,
        sort_by: sortBy,
      });

      const data = response.data;
      setAccounts(data?.objects || []);
      setPagination({
        page: data?.page || 1,
        page_size: data?.page_size || 10,
        count: data?.count || 0,
        objects: data?.objects || [],
      });
    } catch (error) {
      console.error('AccountContext: Error fetching accounts:', error);
      setAccounts([]);
      setPagination(null);
    } finally {
      setIsLoading(false);
    }
  }, [page, pageSize, search, sortBy, isAuthenticated, authLoading, userRole]);

  const refreshAccounts = useCallback(async () => {
    await getAccounts();
  }, [getAccounts]);

  useEffect(() => {
    // Don't fetch accounts for partner role
    if (user?.role === 'partner') {
      return;
    }

    getAccounts();
    const interval = setInterval(() => {
      getAccounts();
    }, 5000);
    return () => clearInterval(interval);
  }, [page, pageSize, search, sortBy, isAuthenticated, authLoading, userRole]);

  const value: AccountContextType = React.useMemo(
    () => ({
      accounts,
      isLoading,
      pagination,
      search,
      page,
      pageSize,
      sortBy,
      refreshAccounts,
      setSearch,
      setPage,
      setPageSize,
      setSortBy,
    }),
    [accounts, isLoading, pagination, search, page, pageSize, sortBy, refreshAccounts]
  );

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
});

AccountProvider.displayName = 'AccountProvider';
